<template>
  <div class="relative flex w-full flex-col space-y-4">
    <div class="flex justify-end">
      <button class="rounded-md p-1 transition hover:bg-obsidian-100" @click="emit('onClose')">
        <XMarkIcon class="size-6 text-red-500" />
      </button>
    </div>
    <div class="flex flex-col space-y-3">
      <Input v-model="damageDescription" :label="t('description')" :placeholder="t('damage')" />
      <p v-if="descriptionError" class="text-xs text-red-600" aria-live="polite">
        {{ descriptionError }}
      </p>

      <div class="w-full space-y-4">
        <h4>{{ t('picture') }}</h4>
        <StatusBanner v-if="isCameraUnavailable" variant="warning">
          {{ t('cameraUnavailable') }}
        </StatusBanner>
        <div v-if="!isInAppCapture || isCameraUnavailable" data-testid="damage-photo-dropzone">
          <UploadDropzone
            :files="photos"
            :empty-hint="t('dropzoneHint')"
            :add-more-hint="t('dropzoneAddMore')"
            :remove-label="t('removePhoto')"
            @update:files="handleDropzoneChange"
          />
        </div>
        <template v-else>
          <div v-if="photos.length" class="flex flex-wrap gap-2">
            <EvidencePreview
              v-for="photo in photos"
              :key="photo.url"
              kind="photo"
              :url="photo.url"
              :status="getItemStatus(photo)"
              @remove="handleRemovePhoto(photo)"
            />
          </div>
          <div class="flex gap-2">
            <CVButton
              class="flex-1"
              :variant="photos.length ? 'secondary' : 'default'"
              @click.prevent="showPhotoCamera = true"
            >
              {{ photos.length ? t('addPicture') : t('takePicture') }}
            </CVButton>
            <CVButton
              variant="secondary"
              size="iconSm"
              :aria-label="t('uploadPicture')"
              :title="t('uploadPicture')"
              @click.prevent="photoUploadInput?.click()"
            >
              <UploadIcon class="size-4" />
            </CVButton>
            <input
              ref="photoUploadInput"
              type="file"
              class="hidden"
              accept="image/*"
              multiple
              @change="handlePhotoUpload"
            />
          </div>
        </template>
        <div v-if="isMobile && !useInAppEvidence" class="flex justify-center">
          <CVButton class="w-full" @click.prevent="startDeviceCamera('imageCamera')">
            {{ t('takePicture') }}
          </CVButton>
          <input
            id="imageCamera"
            type="file"
            capture="environment"
            class="hidden"
            accept="image/*"
            @change="handlePhotoInput"
          />
        </div>
        <DamageEvidenceCamera
          v-if="showPhotoCamera"
          @captured="handleInAppPhoto"
          @close="showPhotoCamera = false"
          @failed="handlePhotoCameraFailed"
        />
        <p v-if="imageError" class="text-xs text-red-600" aria-live="polite">{{ imageError }}</p>

        <h4>{{ t('video') }}</h4>
        <EvidencePreview
          v-if="video"
          kind="video"
          :url="video.url"
          :status="getItemStatus(video)"
          @remove="handleRemoveVideo"
        />
        <StatusBanner v-if="isRecorderUnavailable" variant="warning">
          {{ t('recorderUnavailable') }}
        </StatusBanner>
        <div v-if="!video" class="relative flex items-center gap-3">
          <div v-if="isInAppCapture && !isRecorderUnavailable" class="flex w-full gap-2">
            <CVButton class="flex-1" @click.prevent="showVideoRecorder = true">
              {{ t('takeVideo') }}
            </CVButton>
            <CVButton
              variant="secondary"
              size="iconSm"
              :aria-label="t('uploadVideo')"
              :title="t('uploadVideo')"
              @click.prevent="videoUploadInput?.click()"
            >
              <UploadIcon class="size-4" />
            </CVButton>
            <input
              ref="videoUploadInput"
              type="file"
              class="hidden"
              accept="video/*"
              @change="handleVideoInput"
            />
          </div>
          <div v-else-if="isMobile">
            <CVButton class="w-full" @click.prevent="startDeviceCamera('videoCamera')">
              {{ t('takeVideo') }}
            </CVButton>
            <input
              id="videoCamera"
              type="file"
              capture="environment"
              class="hidden"
              accept="video/*"
              @change="handleVideoInput"
            />
          </div>
          <template v-if="!isInAppCapture || isRecorderUnavailable">
            <div v-if="isMobile && !isInAppCapture">/</div>
            <div @click="isVideoDirty = true">
              <FileInput
                :model-value="null"
                accept="video/*"
                @update:model-value="(file) => file && setVideo(file)"
              />
            </div>
          </template>
        </div>
        <DamageEvidenceRecorder
          v-if="showVideoRecorder"
          @recorded="handleInAppVideo"
          @skip="showVideoRecorder = false"
          @close="showVideoRecorder = false"
          @failed="handleVideoRecorderFailed"
        />
      </div>
    </div>
    <div>
      <h4>{{ t('mark') }}</h4>
      <MarkerContainer
        :marker-positions="damagePosition && [damagePosition]"
        hide-position-numbers
        @select-position="damagePosition = $event"
      />
      <p v-if="markerError" class="text-xs text-red-600" aria-live="polite">{{ markerError }}</p>
    </div>
    <div v-if="newDamage" class="flex justify-end gap-2">
      <CVButton
        variant="default"
        :disabled="isSaving || (!useInAppEvidence && isUploadingMedia)"
        :is-loading="isSaving || (!useInAppEvidence && isUploadingMedia)"
        @click.prevent="save"
      >
        {{ t('save') }}
      </CVButton>
      <CVButton variant="ghost" @click.prevent="close()">{{ t('cancel') }}</CVButton>
    </div>
    <div v-if="genericError" class="text-sm text-red-600" aria-live="polite">
      {{ genericError }}
    </div>
  </div>
</template>

<script setup lang="ts">
import Input from '@/components/Input.vue';
import type { CreateDamageDto, Position } from '@/entities/damage.entity';
import { useI18n } from 'vue-i18n';
import { useCreateDamage } from '@/queries/use-damages';
import { useUploadDamageMedia } from '@/queries/use-upload';
import { Alert } from '@/services/alert.service';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { UploadIcon } from 'lucide-vue-next';
import { computed, onBeforeUnmount, onMounted, reactive, ref, type Ref, watch } from 'vue';
import MarkerContainer from './components/MarkerContainer.vue';
import EvidencePreview from './components/EvidencePreview.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import UploadDropzone, { type PreviewFile } from '@/components/UploadDropzone.vue';
import FileInput from '@/components/FileInput.vue';
import DamageEvidenceCamera from './DamageEvidenceCamera.vue';
import DamageEvidenceRecorder from './DamageEvidenceRecorder.vue';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { compressImageToJpeg } from '@/utils/compress-jpeg';
import StatusBanner from '@/components/StatusBanner.vue';
import { DAMAGE_IN_APP_EVIDENCE_FLAG, isTouchCaptureDevice } from '@/utils/damage-evidence';
import {
  evidenceUploadQueue,
  getDraftStatus,
  type UnsentDamage,
} from '@/utils/evidence-upload-queue';
import { isUnreachableError, OfflineError } from '@/utils/is-unreachable-error';
import { useEvidenceUploadQueue } from '@/hooks/use-evidence-upload-queue';

/**
 * A photo or video in the form, with the preview URL the dropzone and EvidencePreview show. With
 * in-app evidence it is queued as a draft right away, so it uploads while the form is still open.
 */
type EvidenceItem = {
  name: string;
  url: string;
  mimeType: string;
  file: File;
  /** Upload queue item, once compressed and queued. */
  draftId: Promise<string> | null;
};

const props = defineProps<{
  carId: string;
  bookingId?: string;
}>();

const emit = defineEmits<{
  (e: 'onClose', newDamageId?: string): void;
}>();

const { t } = useI18n();
const useInAppEvidence = useFeatureFlag(DAMAGE_IN_APP_EVIDENCE_FLAG);
const isMobile = isTouchCaptureDevice();
/** Phone and tablet with in-app evidence: in-app camera and recorder instead of the dropzone. */
const isInAppCapture = computed(() => !!useInAppEvidence.value && isMobile);

const showPhotoCamera = ref(false);
const photoUploadInput = ref<HTMLInputElement | null>(null);
const videoUploadInput = ref<HTMLInputElement | null>(null);
const showVideoRecorder = ref(false);
/** The recorder opens on its own once, after the first photo; later only on request. */
const hasOfferedVideo = ref(false);
/** The camera or recorder could not start; the form falls back to picking files. */
const isCameraUnavailable = ref(false);
const isRecorderUnavailable = ref(false);
const descriptionError = ref<string | null>(null);
const imageError = ref<string | null>(null);
const markerError = ref<string | null>(null);
const genericError = ref<string | null>(null);
const photos = ref<EvidenceItem[]>([]);
const video = ref<EvidenceItem | null>(null);
const isVideoDirty = ref(false);
/** After saving, the drafts belong to the Damage and must not be discarded with the form. */
let isSaved = false;

const { items: queueItems } = useEvidenceUploadQueue();
const resolvedDraftIds = reactive(new Map<EvidenceItem, string>());
const getItemStatus = (item: EvidenceItem) => {
  if (!useInAppEvidence.value) return undefined;
  const id = resolvedDraftIds.get(item);
  const queued = id ? queueItems.value.find((row) => row.id === id) : undefined;
  return queued ? getDraftStatus(queued) : 'uploading';
};

const createItem = (file: File, kind: 'photo' | 'video', url = URL.createObjectURL(file)) => {
  const item: EvidenceItem = { name: file.name, url, mimeType: file.type, file, draftId: null };
  if (useInAppEvidence.value) {
    const prepared = kind === 'photo' ? compressImageToJpeg(file) : Promise.resolve(file);
    item.draftId = prepared.then((ready) =>
      evidenceUploadQueue.enqueueDraft(props.carId, kind, ready, ready.name || file.name),
    );
    void item.draftId.then((id) => resolvedDraftIds.set(item, id));
  }
  return item;
};

const discardItem = (item: EvidenceItem) => {
  URL.revokeObjectURL(item.url);
  resolvedDraftIds.delete(item);
  void item.draftId?.then((id) => evidenceUploadQueue.discardDrafts([id]));
};

const addPhoto = (file: File, url?: string) => {
  photos.value.push(createItem(file, 'photo', url));
};

const setVideo = (file: File) => {
  isVideoDirty.value = true;
  if (video.value) discardItem(video.value);
  video.value = createItem(file, 'video');
};

const takeInputFile = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.item(0);
  target.value = '';
  return file;
};

const startDeviceCamera = (id: string) => {
  if (id === 'videoCamera') isVideoDirty.value = true;
  document.getElementById(id)?.click();
};

/** The dropzone keeps its own list; mirror additions and removals into the photos. */
const handleDropzoneChange = (files: PreviewFile[]) => {
  const urls = new Set(files.map((file) => file.url));
  for (const photo of photos.value.filter((item) => !urls.has(item.url))) discardItem(photo);
  photos.value = photos.value.filter((photo) => urls.has(photo.url));
  for (const added of files) {
    if (added.file && !photos.value.some((photo) => photo.url === added.url)) {
      addPhoto(added.file, added.url);
    }
  }
};

const handlePhotoInput = (event: Event) => {
  const file = takeInputFile(event);
  if (file) addPhoto(file);
};

/** Picked from files next to the in-app camera; several at once, kept unmarked. */
const handlePhotoUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = Array.from(target.files ?? []);
  target.value = '';
  for (const file of files) addPhoto(file);
};

const handleVideoInput = (event: Event) => {
  const file = takeInputFile(event);
  if (file) setVideo(file);
};

const handleInAppPhoto = (file: File) => {
  addPhoto(file);
  showPhotoCamera.value = false;
  if (!hasOfferedVideo.value && !video.value) {
    hasOfferedVideo.value = true;
    showVideoRecorder.value = true;
  }
};

const handleInAppVideo = (file: File) => {
  setVideo(file);
  showVideoRecorder.value = false;
};

const handlePhotoCameraFailed = () => {
  showPhotoCamera.value = false;
  isCameraUnavailable.value = true;
};

const handleVideoRecorderFailed = () => {
  showVideoRecorder.value = false;
  isRecorderUnavailable.value = true;
};

const handleRemovePhoto = (photo: EvidenceItem) => {
  discardItem(photo);
  photos.value = photos.value.filter((item) => item !== photo);
};

const handleRemoveVideo = () => {
  if (video.value) discardItem(video.value);
  video.value = null;
};

/** Frees the previews; drafts are discarded too unless the Damage was saved with them. */
const releaseItems = () => {
  for (const item of [...photos.value, ...(video.value ? [video.value] : [])]) {
    if (isSaved) URL.revokeObjectURL(item.url);
    else discardItem(item);
  }
};

const damagePosition: Ref<Position | undefined> = ref();
const damageDescription = ref('');

const newDamage = computed((): CreateDamageDto => {
  return {
    carId: props.carId,
    bookingId: props.bookingId ?? undefined,
    description: damageDescription.value,
    positionX: damagePosition.value?.positionX ?? -Infinity,
    positionY: damagePosition.value?.positionY ?? -Infinity,
  };
});

const { mutateAsync: createDamage, isPending: isSaving } = useCreateDamage();
const { mutateAsync: uploadMedia, isPending: isUploadingMedia } = useUploadDamageMedia();

/**
 * Creates the Damage with the drafts that already finished uploading; the queue attaches the rest
 * as soon as they land, so saving never waits for the network.
 */
const saveInBackground = async () => {
  try {
    const items = [...photos.value, ...(video.value ? [video.value] : [])];
    const draftIdOf = new Map<EvidenceItem, string>();
    for (const item of items) draftIdOf.set(item, await item.draftId!);
    const draftIds = [...draftIdOf.values()];
    const uploadedKeys = evidenceUploadQueue.findUploadedDraftKeys(draftIds);
    const findKey = (item: EvidenceItem) => uploadedKeys.get(draftIdOf.get(item)!);
    const damage: UnsentDamage = {
      ...newDamage.value,
      id: crypto.randomUUID(),
      expectedPhotoCount: photos.value.length,
      videoExpected: !!video.value,
    };
    let attachedIds = [...uploadedKeys.keys()];
    try {
      if (!navigator.onLine) throw new OfflineError();
      await createDamage({
        ...damage,
        imageFileKeys: photos.value.flatMap((photo) => findKey(photo) ?? []),
        videoFileKey: video.value ? findKey(video.value) : undefined,
      });
    } catch (error) {
      if (!isUnreachableError(error)) throw error;
      // Kept on the device and sent later with the same id; its evidence attaches afterwards.
      await evidenceUploadQueue.enqueueUnsentDamage(damage);
      attachedIds = [];
    }
    const { id } = damage;
    isSaved = true;
    try {
      await evidenceUploadQueue.linkDrafts(draftIds, id, attachedIds);
    } finally {
      close(id);
    }
  } catch {
    genericError.value = t('errors.saveFailed');
  }
};

const uploadOnSave = async (item: EvidenceItem) => {
  const { fileKey } = await uploadMedia({
    file: item.file,
    params: { carId: props.carId, originalFileName: item.name },
  });
  return fileKey;
};

const close = (newDamageId?: string) => {
  releaseItems();
  photos.value = [];
  video.value = null;
  isVideoDirty.value = false;
  damageDescription.value = '';
  if (damagePosition.value) {
    damagePosition.value.positionX = undefined;
    damagePosition.value.positionY = undefined;
    damagePosition.value = undefined;
  }
  emit('onClose', newDamageId);
};

const save = async () => {
  if (!newDamage.value) return;
  descriptionError.value = null;
  imageError.value = null;
  markerError.value = null;
  genericError.value = null;

  let hasError = false;
  if (!damagePosition.value) {
    markerError.value = t('errors.markerRequired');
    hasError = true;
  }
  if (!damageDescription.value) {
    descriptionError.value = t('errors.descriptionRequired');
    hasError = true;
  }
  if (photos.value.length === 0) {
    imageError.value = t('errors.imageRequired');
    hasError = true;
  }
  if (hasError) return;

  if (useInAppEvidence.value) {
    await saveInBackground();
    return;
  }

  try {
    const imageFileKeys = await Promise.all(photos.value.map(uploadOnSave));
    const videoFileKey = video.value ? await uploadOnSave(video.value) : undefined;

    if (isVideoDirty.value && !videoFileKey) {
      const { isConfirmed } = await Alert.fire({
        icon: 'warning',
        title: t('warnings.videoMissingTitle'),
        text: t('warnings.videoMissingText'),
        showCancelButton: true,
        confirmButtonText: t('saveAnyway'),
        cancelButtonText: t('cancel'),
      });
      if (!isConfirmed) return;
    }

    const { id } = await createDamage({ ...newDamage.value, imageFileKeys, videoFileKey });
    close(id);
  } catch {
    genericError.value = t('errors.saveFailed');
  }
};

watch(damageDescription, (val) => {
  if (val) descriptionError.value = null;
});

watch(
  () => damagePosition.value,
  (val) => {
    if (val) markerError.value = null;
  },
);

watch(
  () => photos.value.length,
  (count) => {
    if (count > 0) imageError.value = null;
  },
);

onMounted(() => {
  if (isInAppCapture.value) showPhotoCamera.value = true;
});

onBeforeUnmount(releaseItems);
</script>

<i18n lang="json">
{
  "en": {
    "description": "Add short description of damage",
    "saveImage": "save image",
    "mark": "Mark the damage",
    "damages": "Damages",
    "damage": "Damage",
    "addDamage": "+ add damage",
    "newDamages": "New Damages",
    "cancelImage": "Cancel",
    "takePicture": "+ Take picture",
    "uploadPicture": "Upload photos",
    "uploadVideo": "Upload video",
    "takeVideo": "+ Take video",
    "picture": "Picture",
    "video": "Video",
    "dropzoneHint": "Drag & drop images here or click to select",
    "dropzoneAddMore": "Drag & drop more images here or click to add more",
    "removePhoto": "Remove",
    "saveAnyway": "Save anyway",
    "warnings": {
      "videoMissingTitle": "Video missing",
      "videoMissingText": "No video was uploaded. Save the damage without a video?"
    },
    "errors": {
      "markerRequired": "Please mark the damage on the sketch before saving.",
      "descriptionRequired": "Please add a short description of the damage.",
      "imageRequired": "Please add a picture of the damage before saving.",
      "saveFailed": "Saving the damage failed. Please try again."
    },
    "addPicture": "+ Another photo",
    "cameraUnavailable": "The camera could not start. Please choose photos from your files.",
    "recorderUnavailable": "The camera could not start for video. You can choose a video file instead."
  },
  "de": {
    "description": "Kurze Beschreibung des Schadens hinzufügen",
    "saveImage": "Bild Speichern",
    "mark": "Markiere den Schaden",
    "damages": "Schäden",
    "damage": "Schaden",
    "addDamage": "+ Schaden hinzufügen",
    "newDamages": "Neue Schäden",
    "cancelImage": "erneut aufnehmen",
    "takePicture": "+ Bild aufnehmen",
    "uploadPicture": "Fotos hochladen",
    "uploadVideo": "Video hochladen",
    "takeVideo": "+ Video aufnehmen",
    "picture": "Bild",
    "video": "Video",
    "dropzoneHint": "Bilder hierher ziehen oder klicken zum Auswählen",
    "dropzoneAddMore": "Weitere Bilder hierher ziehen oder klicken zum Hinzufügen",
    "removePhoto": "Entfernen",
    "saveAnyway": "Trotzdem speichern",
    "warnings": {
      "videoMissingTitle": "Video fehlt",
      "videoMissingText": "Es wurde kein Video hochgeladen. Schaden trotzdem ohne Video speichern?"
    },
    "errors": {
      "markerRequired": "Bitte markiere den Schaden auf der Skizze, bevor du speicherst.",
      "descriptionRequired": "Bitte füge eine kurze Beschreibung des Schadens hinzu.",
      "imageRequired": "Bitte füge ein Bild des Schadens hinzu, bevor du speicherst.",
      "saveFailed": "Speichern des Schadens ist fehlgeschlagen. Bitte versuche es erneut."
    },
    "addPicture": "+ Weiteres Foto",
    "cameraUnavailable": "Die Kamera konnte nicht gestartet werden. Bitte Fotos aus den Dateien wählen.",
    "recorderUnavailable": "Die Kamera konnte für das Video nicht gestartet werden. Du kannst stattdessen eine Videodatei wählen."
  },
  "es": {
    "description": "Añada una breve descripción del daño",
    "saveImage": "guardar imagen",
    "mark": "Marque el daño",
    "damages": "Daños",
    "damage": "Daño",
    "addDamage": "+ añadir daño",
    "newDamages": "Daños nuevos",
    "cancelImage": "Cancelar",
    "takePicture": "+ Hacer foto",
    "uploadPicture": "Subir fotos",
    "uploadVideo": "Subir vídeo",
    "takeVideo": "+ Grabar vídeo",
    "picture": "Foto",
    "video": "Vídeo",
    "dropzoneHint": "Arrastre imágenes aquí o haga clic para seleccionar",
    "dropzoneAddMore": "Arrastre más imágenes aquí o haga clic para añadir",
    "removePhoto": "Eliminar",
    "saveAnyway": "Guardar de todos modos",
    "warnings": {
      "videoMissingTitle": "Falta el vídeo",
      "videoMissingText": "Se inició un vídeo, pero no está disponible. ¿Guardar el daño sin vídeo?"
    },
    "errors": {
      "markerRequired": "Marque el daño en el croquis antes de guardar.",
      "descriptionRequired": "Añada una breve descripción del daño.",
      "imageRequired": "Añada una foto del daño antes de guardar.",
      "saveFailed": "No se pudo guardar el daño. Inténtelo de nuevo."
    },
    "addPicture": "+ Otra foto",
    "cameraUnavailable": "No se pudo iniciar la cámara. Elija las fotos desde sus archivos.",
    "recorderUnavailable": "No se pudo iniciar la cámara para el vídeo. Puede elegir un archivo de vídeo."
  }
}
</i18n>
